import classnames from 'classnames';
import React, { FC } from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { runningInRNWebView } from '@eika-infrastruktur/mobilbank-lib';
import { IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';

import { useConfig } from '../../defaultConfig';
import type { Account } from '../../generated-types';
import useNextSalary from '../../hooks/useNextSalary';
import useTranslation from '../../hooks/useTranslation';
import { accessibileAccountNumber, accessibleCurrency } from '../../services/accessibilityService';
import FavoriteAccountSelector from '../../widgets/FavoriteAccount/FavoriteAccountSelector';
import SalaryInfoMobile from '../SalaryInfo/SalaryInfoMobile';
import { currencyFormatter } from '../shared/numberFormat';

import './FavoriteAccount.scss';

export interface FavoriteAccountProps {
    className?: string;
    account?: Account;
}

const FavoriteAccount: FC<FavoriteAccountProps> = ({ className, account }) => {
    const translated = useTranslation();
    const { accountLink } = useConfig();

    const { nextSalaryDetails, salaryAccount, nextSalaryError } = useNextSalary();

    if (nextSalaryDetails && salaryAccount && !nextSalaryError) {
        return <SalaryInfoMobile salaryDetails={nextSalaryDetails} salaryAccount={salaryAccount} />;
    }

    if (!account) {
        return (
            <FavoriteAccountSelector className={classnames({ native: runningInRNWebView })} error={nextSalaryError} />
        );
    }

    return (
        <a
            className={classnames('sgw-favorite-account', className)}
            href={`${accountLink?.url}?id=${account.accountId.value}`}
        >
            <dl
                className={classnames('sgw-favorite-account__account-details', {
                    'sgw-favorite-account__account-details--error': nextSalaryError,
                })}
                itemScope
                itemType="https://schema.org/DepositAccount"
            >
                <dt>Kontonavn</dt>
                <dd
                    data-testid="favorite-account-name"
                    itemProp="name"
                    className="typography-subtitle"
                    aria-label={`Favorittkonto ${account.alias}`}
                >
                    {account.alias}
                </dd>

                <dt>Kontonummer</dt>
                <dd
                    itemProp="accountId"
                    itemScope
                    itemType="https://schema.org/accountId"
                    aria-label={`Kontonummer ${accessibileAccountNumber(formatAccountNumber(account.accountNumber))}`}
                    data-testid="favorite-account-number"
                >
                    {formatAccountNumber(account.accountNumber)}
                </dd>

                <dt>{translated.accountView.disposible}</dt>
                <dd
                    itemProp="amount"
                    itemScope
                    itemType="https://schema.org/MonetaryAmount"
                    aria-label={`${translated.accountView.disposible} ${accessibleCurrency(
                        account.balance.availableBalance,
                        account.currency,
                    )}`}
                >
                    <span
                        data-testid="visible-balance"
                        itemProp="value"
                        {...{ content: String(account.balance.availableBalance) }}
                        aria-label={accessibleCurrency(account.balance.availableBalance, account.currency, 0)}
                    >
                        {currencyFormatter(Math.floor(account.balance.availableBalance), account.currency, 0)}
                    </span>
                    <meta itemProp="currency" content={account.currency.toString()} />
                </dd>
            </dl>
            {nextSalaryError && (
                <ErrorLabel
                    severe
                    className="sgw-favorite-account--error"
                    text={translated.commonErrors.paymentAndNextSalaryFailed}
                    icon={IconTypes.UTROPSTEGN_24}
                />
            )}
        </a>
    );
};

export default FavoriteAccount;
